// AvoirPrintUtility.js

const formatDate = (date) => date ? new Date(date).toLocaleDateString('fr-FR') : 'Non validé';
const formatNumber = (num) => (Number(num) || 0).toFixed(2);

const template = `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <title>Avoir - {{REF_AVR}}</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            font-size: 12px;
            line-height: 1.4;
        }
        .invoice-container { 
            max-width: 900px;
            margin: 0 auto; 
        }
        .header { 
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
        }
        .box { 
            border: 1px solid #000; 
            padding: 10px;
            margin-bottom: 15px;
            border-radius: 4px;
        }
        .content-table { 
            width: 100%; 
            border-collapse: collapse; 
            margin: 20px 0;
        }
        .content-table th, .content-table td { 
            border: 1px solid #000; 
            padding: 8px;
        }
        .content-table th { 
            background-color: #f5f5f5;
            font-weight: bold;
        }
        .price-column { text-align: right; }
        .summary-table {
            width: 300px;
            margin-left: auto;
            border-collapse: collapse;
        }
        .summary-table th, .summary-table td {
            border: 1px solid #000;
            padding: 8px;
        }
        .summary-table th { text-align: left; }
        .summary-table td { text-align: right; }
        .footer {
            margin-top: 30px;
            border-top: 1px solid #000;
            padding-top: 10px;
        }
        .bold { font-weight: bold; }
        .text-right { text-align: right; }
        .company-info { text-align: right; }
        .avoir-title {
            font-size: 24px;
            margin-bottom: 10px;
            color: #333;
        }
        .validation-stamp {
            color: green;
            font-weight: bold;
            font-size: 14px;
            margin-top: 10px;
        }
        .negative-amount {
            color: black;
        }
        @media print {
            @page { size: A4; margin: 1cm; }
            body { -webkit-print-color-adjust: exact !important; }
        }
    </style>
</head>
<body>
    <div class="invoice-container">
        <!-- Header Section -->
        <div class="header">
            <div>
                <h1 class="avoir-title">AVOIR</h1>
                <div>N° {{REF_AVR}}</div>
                <div>Date: {{DATE_AVR}}</div>
                <div>Validation: {{DATEVALID}}</div>
                <div>Facture de référence: {{REF_FAC}}</div>
            </div>
            <div class="company-info">
                <div class="bold">VOTRE ENTREPRISE</div>
                <div>123 Rue Example</div>
                <div>Ville, Pays</div>
                <div>Tel: +212 XXXXXXXXX</div>
                <div>Email: contact@company.com</div>
            </div>
        </div>

        <!-- Client Information -->
        <div class="box">
            <div style="display: flex; justify-content: space-between;">
                <div>
                    <div class="bold">CLIENT</div>
                    <div>{{CLIENT}}</div>
                    <div>Account: {{COMPTE}}</div>
                    <div>ICE: {{ICE}}</div>
                </div>
                <div>
                    <div>Commercial: {{COMMERCIAL}}</div>
                </div>
            </div>
        </div>

        <!-- Items Table -->
        <table class="content-table">
            <thead>
                <tr>
                    <th style="width: 30%;">Description</th>
                    <th style="width: 15%;">Référence</th>
                    <th style="width: 15%;">Réf</th>
                    <th class="price-column">Quantité</th>
                    <th class="price-column">Prix Unit. HT</th>
                    <th class="price-column">TVA %</th>
                    <th class="price-column">Remise %</th>
                    <th class="price-column">Total HT</th>
                </tr>
            </thead>
            <tbody>
                {{ITEMS_TABLE}}
            </tbody>
        </table>

        <!-- Totals -->
        <table class="summary-table">
            <tr>
                <th>Total HT</th>
                <td class="negative-amount">-{{MNT_HT}} MAD</td>
            </tr>
            <tr>
                <th>Total TVA</th>
                <td class="negative-amount">-{{TVA_TOTAL}} MAD</td>
            </tr>
            <tr>
                <th>Total TTC</th>
                <td class="negative-amount">-{{MNT_TTC}} MAD</td>
            </tr>
        </table>

        <!-- Notes -->
        <div class="box" style="margin-top: 20px;">
            <div><strong>Remarques:</strong> {{REMARQUE}}</div>
        </div>

        {{#if VALIDER}}
        <div class="validation-stamp">
            ✓ Avoir Validé
        </div>
        {{/if}}

        <div class="footer">
            <div style="text-align: center; font-size: 10px;">
                <p>Merci de votre compréhension</p>
            </div>
        </div>
    </div>
</body>
</html>
`;

const createItemsTable = (items) => {
    return items.map(item => {
        const unitPriceHT = item.PV_HT;
        const totalHT = (item.QTE * unitPriceHT * (1 - item.REMISE / 100));
        
        return `
            <tr>
                <td>${item.ARTICLE}</td>
                <td>${item.CODE_ART}</td>
                <td>${item.REF_OEM || item.article?.REF_OEM || ''}</td>
                <td class="price-column">${item.QTE}</td>
                <td class="price-column">${formatNumber(unitPriceHT)}</td>
                <td class="price-column">${formatNumber(item.TVA)}%</td>
                <td class="price-column">${formatNumber(item.REMISE)}%</td>
                <td class="price-column" class="negative-amount">-${formatNumber(totalHT)}</td>
            </tr>
        `;
    }).join('');
};

const processTemplate = (template, replacements) => {
    let processedTemplate = template;
    Object.entries(replacements).forEach(([key, value]) => {
        const regex = new RegExp(`{{${key}}}`, 'g');
        processedTemplate = processedTemplate.replace(regex, value || '');
    });
    
    if (replacements.VALIDER) {
        processedTemplate = processedTemplate.replace('{{#if VALIDER}}', '');
        processedTemplate = processedTemplate.replace('{{/if}}', '');
    } else {
        const regex = /{{#if VALIDER}}[\s\S]*?{{\/if}}/g;
        processedTemplate = processedTemplate.replace(regex, '');
    }
    
    return processedTemplate;
};

const createPrintFrame = (html) => {
    const iframe = document.createElement('iframe');
    iframe.style.visibility = 'hidden';
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    document.body.appendChild(iframe);
    
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
    
    return iframe;
};

export const printAvoir = async (avoir, items, commercial) => {
    try {
        const cleanAvoir = {
            ...avoir,
            MNT_HT: Math.abs(Number(avoir.MNT_HT)) || 0,
            MNT_TTC: Math.abs(Number(avoir.MNT_TTC)) || 0
        };

        const cleanItems = items.map(item => ({
            ...item,
            QTE: Number(item.QTE) || 0,
            PV_HT: Number(item.PV_HT) || 0,
            PV_TTC: Number(item.PV_TTC) || 0,
            REMISE: Number(item.REMISE) || 0,
            TVA: Number(item.TVA) || 0
        }));

        const replacements = {
            REF_AVR: cleanAvoir.REF_AVR,
            REF_FAC: cleanAvoir.REF_FAC || 'N/A',
            DATE_AVR: formatDate(cleanAvoir.DATE_AVR),
            DATEVALID: formatDate(cleanAvoir.DATEVALID),
            CLIENT: cleanAvoir.CLIENT || 'N/A',
            COMPTE: cleanAvoir.COMPTE || 'N/A',
            ICE: cleanAvoir.ICE || 'N/A',
            COMMERCIAL: commercial?.nom || cleanAvoir.CODE_COM,
            REMARQUE: cleanAvoir.REMARQUE || '',
            MNT_HT: formatNumber(cleanAvoir.MNT_HT),
            MNT_TTC: formatNumber(cleanAvoir.MNT_TTC),
            TVA_TOTAL: formatNumber(cleanAvoir.MNT_TTC - cleanAvoir.MNT_HT),
            ITEMS_TABLE: createItemsTable(cleanItems),
            VALIDER: cleanAvoir.VALIDER,
        };

        const html = processTemplate(template, replacements);
        const iframe = createPrintFrame(html);

        iframe.onload = () => {
            iframe.contentWindow.print();
            setTimeout(() => {
                document.body.removeChild(iframe);
            }, 1000);
        };
    } catch (error) {
        console.error('Error printing avoir:', error);
        throw new Error(`Failed to print avoir: ${error.message}`);
    }
};

// Number to French words conversion
const numberToFrenchWords = (num) => {
    const units = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf'];
    const teens = ['dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
    const tens = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante-dix', 'quatre-vingt', 'quatre-vingt-dix'];
    
    if (num === 0) return 'zéro';
    if (num < 10) return units[num];
    if (num < 20) return teens[num - 10];
    if (num < 100) {
        const ten = Math.floor(num / 10);
        const unit = num % 10;
        if (ten === 7) return 'soixante-' + teens[unit];
        if (ten === 9) return 'quatre-vingt-' + teens[unit];
        return tens[ten] + (unit ? '-' + units[unit] : '');
    }
    
    let result = '';
    const hundreds = Math.floor(num / 100);
    const remainder = num % 100;
    
    if (hundreds > 1) result += units[hundreds] + ' cent';
    else if (hundreds === 1) result += 'cent';
    
    if (remainder > 0) {
        if (result) result += ' ';
        result += numberToFrenchWords(remainder);
    }
    
    return result;
};

// État 1 Template (Based on Facture design)
const avoirEtat1Template = `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <title>AVOIR - {{REF_AVR}}</title>
    <style>
        @media print {
  .page { 
    page-break-after: always;
    padding-top: 30mm !important;
    box-sizing: border-box;
  }
}
            @page { size: A4; margin: 1cm 1cm 2.5cm 1cm; }
        }
        body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 5px;
            font-size: 2px;
            line-height: 1;
        }
        .header-container {
            display: flex;
            justify-content: space-between;
            margin-bottom: 10px;
            font-size: 11px;
        }
         .header-right {
            border: 1px solid black;
            padding: 5px;
            border-radius: 8px;
            overflow: hidden;
        }
        .header-left {
            width: 45%;
        }
        .header-right {
            width: 45%;
            text-align: left;
            padding: 10px;
        }

        .avoir-title {
            display: flex;
            border: 1px solid black;
            border-radius: 8px;
            overflow: hidden;
            margin-bottom: 5px;
        }
        
        .avoir-title div {
            padding: 8px 10px;
            font-weight: bold;
        }
        
        .avoir-title div:first-child {
            flex: 1;
            border-right: 1px solid black;
        }
        
        .avoir-title div:last-child {
            flex: 1;
            text-align: center;
        }
            
        .info-grid {
            display: grid;
            grid-template-columns: auto auto auto;
            border: 1px solid black;
            margin: 5px 0;
            border-radius: 8px;
            overflow: hidden;
        }
        .info-grid > div {
            border: 1px solid black;
            padding: 3px 5px;
        }
        .delivery-info {
            display: grid;
            grid-template-columns: 1fr 1fr 1fr 1fr;
            border: 1px solid black;
            margin: 5px 0;
            border-radius: 8px;
            overflow: hidden;
            font-size: 11px;
        }
        .delivery-info > div {
            border: 1px solid black;
            padding: 3px 5px;
        }
        table.content-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
            border-bottom: 1px solid black;
        }
        .content-table th, .content-table td {
            border-style: none solid;
            border-width: 0 1px;
            border-color: black;
            padding: 2px 3px;
            font-size: 11px;
            line-height: 1;
            height: 16px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .content-table th {
            background: #fff;
            font-weight: normal;
            height: 18px;
            border: 1px solid black;
        }
        .content-table td:nth-child(2) {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            max-width: 300px;
        }
        .right { text-align: right; }
        .totals-section {
            margin-top: 10px;
            border-collapse: separate;
            border-spacing: 0;
            width: 100%;
            border-radius: 8px;
            overflow: hidden;
            font-size: 11px;
        }
        .totals-section td {
            border: 1px solid black;
            padding: 3px;
        }
        .amount-in-words {
            margin-top: 10px;
            font-style: italic;
            font-size: 10px;
        }
        .footer-note {
            margin-top: 5px;
            margin-bottom: 15px;
            font-size: 9px;
            font-style: italic;
        }
        .header-right {
            position: relative;
        }
        .ice-bottom {
            position: absolute;
            bottom: 20px;
            left: 10px;
            width: calc(100% - 20px);
            padding: 8px;
            border-radius: 4px;
            margin-top: 10px;
        }
        .negative-amount {
            color: black;
        }
    </style>
</head>
<body>
    {{PAGES}}
</body>
</html>`;

const avoirEtat1PageTemplate = `
<div class="page">
    <div class="header-container">
        <div class="header-left">
        <div class="avoir-title">
            <div>AVOIR N° :</div>
            <div>{{REF_AVR}}</div>
        </div>
        <div class="info-grid">
            <div>Date</div>
            <div>Code client</div>
            <div>N° page</div>
            <div>{{DATE_AVR}}</div>
            <div>{{CODE_CLIENT}}</div>
            <div>{{CURRENT_PAGE}} / {{TOTAL_PAGES}}</div>
        </div>
    </div>
        <div class="header-right">
            <div>
                {{CLIENT}}<br>
                {{CLIENT_ADDRESS}}
            </div>
            <div class="ice-bottom">
                {{ICE}}
            </div>
        </div>
    </div>
    
   

    <table class="content-table">
        <thead>
            <tr>
            <th>Désignation</th>
                <th>Référence</th>
                
                
                <th class="right">Qté</th>
                <th class="right">PU ttc</th>
                <th class="right">R%</th>
                <th class="right">Mnt ttc</th>
            </tr>
        </thead>
        <tbody>
            {{ITEMS_ROWS}}
        </tbody>
    </table>
    {{LAST_PAGE_CONTENT}}
</div>`;

// État 3 Template (Based on Bon de Sortie design)
const avoirEtat3Template = `
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8" />
  <title>Avoir - {{REF_AVR}}</title>
  <style>
    @media print {
      .bonsortie-doc.page { 
        page-break-after: always;
        padding-top: 30mm !important;
        box-sizing: border-box;
      }
    }
    @page { size: A4; margin: 1cm; }
    
    .bonsortie-doc * {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }
    body {
      font-family: Arial, sans-serif;
      color: #000;
      margin: 0;
      padding: 5px;
    }
    .bonsortie-doc-table {
      width: 95%;
      margin: 0 auto;
      border-collapse: collapse;
      font-size: 11px;
    }
    .bonsortie-doc .header-content {
      padding: 10px 5px; 
      border: none;
    }
    .bonsortie-doc .header-line {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .bonsortie-doc th {
      border: none;
      padding: 5px 3px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 120px;
      background: #f0f0f0;
      font-size: 10px;
      font-weight: bold;
      text-transform: uppercase;
    }
    .bonsortie-doc td {
      border: none;
      padding: 4px 3px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 120px;
    }
    .bonsortie-doc .qte-col {
      padding-right: 15px;
    }
    .bonsortie-doc .designation-col {
      padding-left: 15px;
    }
    .bonsortie-doc .prix-col {
      padding-right: 15px;
    }
    .bonsortie-doc .remise-col {
      padding-left: 15px;
    }
    .bonsortie-doc thead tr:last-child {
      border-bottom: 2px solid #000;
    }
    .bonsortie-doc .item-row {
      border-bottom: 1px solid #f0f0f0;
    }
    .bonsortie-doc .right {
      text-align: right;
      max-width: 60px;
    }
    .bonsortie-doc .left {
      text-align: left;
    }
    .bonsortie-doc .total-section {
      text-align: right;
      font-weight: bold;
      border-top: 2px solid #000;
      padding: 10px 6px 6px;
      font-size: 11px;
      margin-top: 5px;
    }
    .footer-note {
      margin-top: 5px;
      font-size: 9px;
      font-style: italic;
    }
    .negative-amount {
      color: red;
    }
  </style>
</head>
<body>
  {{PAGES}}
</body>
</html>`;

const avoirEtat3PageTemplate = `
<div class="bonsortie-doc page">
  <table class="bonsortie-doc-table">
    <thead>
      <tr>
        <td colspan="6" class="header-content">
          <div class="header-line">
            <div>
              <div>Avoir N°: {{REF_AVR}}</div>
              <div>{{CLIENT}} {{COMPTE}}</div>
            </div>
            <div>
              <div>Date: {{DATE_AVR}}</div>
              <div>Page: {{CURRENT_PAGE}} / {{TOTAL_PAGES}}</div>
            </div>
          </div>
        </td>
      </tr>
      <tr>
        <th class="right qte-col" style="width: 3%">Qté</th>
        <th class="left designation-col" style="width: 40%">Désignation</th>
        
        <th class="left" style="width: 12%">Réf</th>
        <th class="right prix-col" style="width: 10%">Prix U</th>
        <th class="left remise-col" style="width: 7%">R %</th>
        <th class="right" style="width: 10%">Total</th>
      </tr>
    </thead>
    <tbody>
      {{ITEMS_ROWS}}
    </tbody>
    {{FOOTER}}
  </table>
  <div class="footer-note">
    Avoir émis conformément aux conditions générales de vente.
  </div>
</div>`;

// Helper functions for new templates
const calculateTotalTTC = (qte, pvTTC, remise) => {
  const discountedPrice = pvTTC * (1 - remise / 100);
  const total = qte * discountedPrice;
  return Math.round(total * 100) / 100;
};

const generateEtat1Pages = (avoir, items) => {
    const ROWS_PER_PAGE = 25;
    const totalPages = Math.ceil(items.length / ROWS_PER_PAGE);
    let pages = [];

    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const startIndex = (pageNum - 1) * ROWS_PER_PAGE;
        const endIndex = Math.min(startIndex + ROWS_PER_PAGE, items.length);
        const pageItems = items.slice(startIndex, endIndex);

        const itemsRows = pageItems.map(item => {
            const totalTTC = calculateTotalTTC(item.QTE, item.PV_TTC, item.REMISE);
            return `
                <tr>
                    
                    <td>${item.ARTICLE}</td>
                    <td>${item.REF_OEM || item.article?.REF_OEM || ''}</td>
                    <td class="right">${item.QTE}</td>
                    <td class="right">${formatNumber(item.PV_TTC)}</td>
                    <td class="right">${formatNumber(item.REMISE)}%</td>
                    <td class="right">-${formatNumber(totalTTC)}</td>
                </tr>
            `;
        }).join('');

        let lastPageContent = '';
        if (pageNum === totalPages) {
            lastPageContent = `
                <table class="totals-section">
                    <tr>
                        <td style="width: 70%; text-align: left;">Total HT</td>
                        <td style="width: 30%; text-align: right;">-${formatNumber(Math.abs(avoir.MNT_HT))} MAD</td>
                    </tr>
                    <tr>
                        <td style="text-align: left;">Total TTC</td>
                        <td style="text-align: right;">-${formatNumber(Math.abs(avoir.MNT_TTC))} MAD</td>
                    </tr>
                </table>
                <div class="amount-in-words">
                    Arrêté le présent avoir à la somme de : ${numberToFrenchWords(Math.floor(Math.abs(avoir.MNT_TTC)))} dirhams ${numberToFrenchWords(Math.round((Math.abs(avoir.MNT_TTC) % 1) * 100))} centimes (crédit)
                </div>
                <div class="footer-note">
                    Avoir émis conformément à la réglementation en vigueur.
                </div>
            `;
        }

        const pageHtml = processTemplate(avoirEtat1PageTemplate, {
            REF_AVR: avoir.REF_AVR,
            DATE_AVR: formatDate(avoir.DATE_AVR),
            CODE_CLIENT: avoir.COMPTE || 'N/A',
            CURRENT_PAGE: pageNum,
            TOTAL_PAGES: totalPages,
            CLIENT: avoir.CLIENT || 'N/A',
            CLIENT_ADDRESS: avoir.CLIENT_ADDRESS || '',
            ICE: avoir.ICE || '',
            REF_FAC: avoir.REF_FAC || 'N/A',
            COMMERCIAL: avoir.COMMERCIAL || 'N/A',
            MODELIV: avoir.MODELIV || 'N/A',
            MODE_PAIE: avoir.MODE_PAIE || 'N/A',
            ITEMS_ROWS: itemsRows,
            LAST_PAGE_CONTENT: lastPageContent
        });

        pages.push(pageHtml);
    }

    return pages.join('');
};

const generateEtat3Pages = (avoir, items) => {
    const ROWS_PER_PAGE = 37;
    const totalPages = Math.ceil(items.length / ROWS_PER_PAGE);
    let pages = [];

    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const startIndex = (pageNum - 1) * ROWS_PER_PAGE;
        const endIndex = Math.min(startIndex + ROWS_PER_PAGE, items.length);
        const pageItems = items.slice(startIndex, endIndex);

        const itemsRows = pageItems.map(item => {
            const totalTTC = calculateTotalTTC(item.QTE, item.PV_TTC, item.REMISE);
            return `
                <tr class="item-row">
                    <td class="right">${item.QTE}</td>
                    <td class="left">${item.ARTICLE}</td>
                  
                    <td class="left">${item.REF_OEM || item.article?.REF_OEM || ''}</td>
                    <td class="right">${formatNumber(item.PV_TTC)}</td>
                    <td class="left">${formatNumber(item.REMISE)}%</td>
                    <td class="right">-${formatNumber(totalTTC)}</td>
                </tr>
            `;
        }).join('');

        let footer = '';
        if (pageNum === totalPages) {
            footer = `
                <tr>
                    <td colspan="6" class="total-section">
                        Total HT: -${formatNumber(Math.abs(avoir.MNT_HT))} MAD | 
                        Total TTC: -${formatNumber(Math.abs(avoir.MNT_TTC))} MAD
                    </td>
                </tr>
            `;
        }

        const pageHtml = processTemplate(avoirEtat3PageTemplate, {
            REF_AVR: avoir.REF_AVR,
            DATE_AVR: formatDate(avoir.DATE_AVR),
           
            CLIENT:avoir.CLIENT || 'N/A',
            COMPTE: avoir.COMPTE || 'N/A',
            CURRENT_PAGE: pageNum,
            TOTAL_PAGES: totalPages,
            ITEMS_ROWS: itemsRows,
            FOOTER: footer
        });

        pages.push(pageHtml);
    }

    return pages.join('');
};

export const printAvoirEtat1 = async (avoir, items, commercial) => {
    try {
        const cleanAvoir = {
            ...avoir,
            MNT_HT: Math.abs(Number(avoir.MNT_HT)) || 0,
            MNT_TTC: Math.abs(Number(avoir.MNT_TTC)) || 0,
            COMMERCIAL: commercial?.nom || avoir.CODE_COM || 'N/A'
        };

        const cleanItems = items.map(item => ({
            ...item,
            QTE: Number(item.QTE) || 0,
            PV_HT: Number(item.PV_HT) || 0,
            PV_TTC: Number(item.PV_TTC) || 0,
            REMISE: Number(item.REMISE) || 0,
            TVA: Number(item.TVA) || 0
        }));

        // État 1 uses bon de sortie design (clean, borderless)
        const pagesHtml = generateEtat3Pages(cleanAvoir, cleanItems);
        const html = processTemplate(avoirEtat3Template, {
            REF_AVR: cleanAvoir.REF_AVR,
            PAGES: pagesHtml
        });

        const iframe = createPrintFrame(html);
        iframe.onload = () => {
            iframe.contentWindow.print();
            setTimeout(() => {
                document.body.removeChild(iframe);
            }, 1000);
        };
    } catch (error) {
        console.error('Error printing avoir état 1:', error);
        throw new Error(`Failed to print avoir état 1: ${error.message}`);
    }
};

export const printAvoirEtat3 = async (avoir, items, commercial) => {
    try {
        const cleanAvoir = {
            ...avoir,
            MNT_HT: Math.abs(Number(avoir.MNT_HT)) || 0,
            MNT_TTC: Math.abs(Number(avoir.MNT_TTC)) || 0,
            COMMERCIAL: commercial?.nom || avoir.CODE_COM || 'N/A',
            MODELIV: 'Livraison standard',
            MODE_PAIE: 'Selon conditions'
        };

        const cleanItems = items.map(item => ({
            ...item,
            QTE: Number(item.QTE) || 0,
            PV_HT: Number(item.PV_HT) || 0,
            PV_TTC: Number(item.PV_TTC) || 0,
            REMISE: Number(item.REMISE) || 0,
            TVA: Number(item.TVA) || 0
        }));

        // État 3 uses facture design (bordered, professional)
        const pagesHtml = generateEtat1Pages(cleanAvoir, cleanItems);
        const html = processTemplate(avoirEtat1Template, {
            REF_AVR: cleanAvoir.REF_AVR,
            PAGES: pagesHtml
        });

        const iframe = createPrintFrame(html);
        iframe.onload = () => {
            iframe.contentWindow.print();
            setTimeout(() => {
                document.body.removeChild(iframe);
            }, 1000);
        };
    } catch (error) {
        console.error('Error printing avoir état 3:', error);
        throw new Error(`Failed to print avoir état 3: ${error.message}`);
    }
};

// PC Control Template (Based on the document image)
const avoirPcControlTemplate = `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <title>Pc controle Avoir - {{REF_AVR}}</title>
    <style>
        @media print {
            @page { size: A4; margin: 1cm; }
            body { -webkit-print-color-adjust: exact !important; }
        }
        body {
            font-family: 'Courier New', monospace;
            margin: 0;
            padding: 15px;
            font-size: 12px;
            line-height: 1.2;
            background-color: #f9f9f9;
        }
        .pc-control-container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            padding: 20px;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        .header-title {
            text-align: center;
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 20px;
            text-decoration: underline;
            letter-spacing: 2px;
        }
        .document-info .row {
            display: flex;
            margin-bottom: 8px;
        }
        .document-info .label {
            font-weight: bold;
            width: 120px;
        }
        .items-table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
            font-family: 'Courier New', monospace;
        }
        .items-table th {
            border: 1px solid #000;
            padding: 8px 5px;
            background-color: #f0f0f0;
            font-weight: bold;
            text-align: center;
            font-size: 11px;
        }
        .items-table td {
            border: 1px solid #000;
            padding: 5px;
            font-size: 10px;
        }
        .items-table .ref-col { width: 10%; text-align: left; }
        .items-table .ref-oem-col { width: 10%; text-align: left; }
        .items-table .desc-col { width: 35%; text-align: left; }
        .items-table .qty-col { width: 8%; text-align: center; }
        .items-table .price-col { width: 12%; text-align: right; }
        .items-table .disc-col { width: 8%; text-align: center; }
        .items-table .total-col { width: 17%; text-align: right; }
        .total-section {
            margin-top: 20px;
            text-align: right;
        }
        .total-amount {
            display: inline-block;
            background-color: #ff69b4;
            color: white;
            padding: 8px 15px;
            border-radius: 15px;
            font-weight: bold;
            font-size: 14px;
        }
        .info-box {
            border: 1px solid #000;
            padding: 10px;
            margin: 10px 0;
            background-color: #fafafa;
        }
        .info-section {
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;
        }
        .document-info {
            flex: 1;
        }
        .page-number-box {
            border: 1px solid #000;
            padding: 15px;
            width: 150px;
            height: 100px;
            text-align: center;
        }
        .page-number {
            font-size: 12px;
            font-weight: bold;
            margin-bottom: 10px;
        }
        .dotted-lines-top {
            font-size: 10px;
            line-height: 1.6;
            color: #666;
        }
    </style>
</head>
<body>
    
        <div class="header-title">Pc controle Avoir</div>
        
        <!-- Info section with document info on left and page number box on right -->
        <div class="info-section">
            <div class="document-info">
                <div class="row">
                    <span class="label">AVOIR</span>
                    <span>{{REF_AVR}}</span>
                </div>
                <div class="row">
                    <span class="label">{{CLIENT_CODE}} - {{CLIENT_NAME}}</span>
                </div>
                <div class="row">
                    <span class="label">{{DATE_AVR}}</span>
                    <span style="margin-left: 100px;">{{PAGE_INFO}}</span>
                </div>
            </div>
            
            <div class="page-number-box">
                <div class="page-number">{{PAGE_NUMBER}}</div>
                <div class="dotted-lines-top">
                    .......................<br>
                    .......................<br>
                    .......................
                </div>
            </div>
        </div>

        <table class="items-table">
            <thead>
                <tr>
                    
                    <th class="ref-col">Code article</th>
                    <th class="desc-col">Désignation</th>
                    <th class="ref-oem-col">Réf</th>
                    <th class="qty-col">Qté</th>
                    <th class="price-col">Px</th>
                    <th class="disc-col">R%</th>
                    <th class="total-col">Mnt</th>
                </tr>
            </thead>
            <tbody>
                {{ITEMS_ROWS}}
            </tbody>
        </table>

        <div class="total-section">
            <div>Total</div>
            <div class="total-amount">{{TOTAL_AMOUNT}}</div>
        </div>
    
</body>
</html>`;

const generatePcControlItemsRows = (items) => {
    return items.map(item => {
        const totalAmount = (item.QTE * item.PV_TTC * (1 - item.REMISE / 100));
        return `
            <tr>
                <td class="ref-col">${item.CODE_ART}</td>
                <td class="desc-col">${item.ARTICLE}</td>
                <td class="ref-oem-col">${item.REF_OEM || item.article?.REF_OEM || ''}</td>
                <td class="qty-col">${item.QTE}</td>
                <td class="price-col">${formatNumber(item.PV_TTC)}</td>
                <td class="disc-col">${formatNumber(item.REMISE)}</td>
                <td class="total-col">${formatNumber(totalAmount)}</td>
            </tr>
        `;
    }).join('');
};

export const printAvoirPcControl = async (avoir, items, commercial) => {
    try {
        const cleanAvoir = {
            ...avoir,
            MNT_HT: Math.abs(Number(avoir.MNT_HT)) || 0,
            MNT_TTC: Math.abs(Number(avoir.MNT_TTC)) || 0
        };

        const cleanItems = items.map(item => ({
            ...item,
            QTE: Number(item.QTE) || 0,
            PV_HT: Number(item.PV_HT) || 0,
            PV_TTC: Number(item.PV_TTC) || 0,
            REMISE: Number(item.REMISE) || 0,
            TVA: Number(item.TVA) || 0
        }));

        // Calculate pagination
        const ROWS_PER_PAGE = 25; // You can adjust this based on how many items fit per page
        const totalPages = Math.ceil(cleanItems.length / ROWS_PER_PAGE);
        const currentPage = 1; // For now, showing page 1. You can make this dynamic if needed

        const replacements = {
            REF_AVR: cleanAvoir.REF_AVR,
            CLIENT_CODE: cleanAvoir.COMPTE || 'N/A',
            CLIENT_NAME: cleanAvoir.CLIENT || 'N/A',
            DATE_AVR: formatDate(cleanAvoir.DATE_AVR),
            PAGE_NUMBER: String(currentPage).padStart(2, '0'),
            PAGE_INFO: `${currentPage} / ${totalPages}`,
            ITEMS_ROWS: generatePcControlItemsRows(cleanItems),
            TOTAL_AMOUNT: formatNumber(cleanAvoir.MNT_TTC)
        };

        const html = processTemplate(avoirPcControlTemplate, replacements);
        const iframe = createPrintFrame(html);

        iframe.onload = () => {
            iframe.contentWindow.print();
            setTimeout(() => {
                document.body.removeChild(iframe);
            }, 1000);
        };
    } catch (error) {
        console.error('Error printing avoir PC Control:', error);
        throw new Error(`Failed to print avoir PC Control: ${error.message}`);
    }
};

// Price Control (Historique) Template similar to provided sample image
const priceControlTemplate = `
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8" />
  <title>Contrôle prix d'AVOIR - {{REF_AVR}}</title>
  <style>
    @media print { @page { size: A4; margin: 1cm; } }
    body { font-family: Arial, sans-serif; font-size: 11px; color: #000; margin: 0; padding: 16px; }
    .header-line { display: flex; justify-content: space-between; margin-bottom: 6px; }
    .title { font-weight: bold; font-size: 16px; margin-bottom: 8px; }
    .meta { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px 16px; margin-bottom: 12px; }
    .meta div span { font-weight: bold; }
    .section { margin-top: 8px; }
    .section-header { font-weight: bold; border-bottom: 1px solid #000; padding: 6px 0; display:flex; align-items:center; gap:8px; }
    .section-header .num { width: 18px; text-align: right; }
    .section-header .code { min-width: 120px; font-family: "Courier New", monospace; }
    .section-header .desc { flex: 1; }
    .section-header .qte { width: 60px; text-align: right; }
    table { width: 100%; border-collapse: collapse; margin-top: 6px; }
    th, td { border-bottom: 1px solid #ddd; padding: 4px 6px; }
    th { background: #f2f2f2; text-align: left; }
    td.right, th.right { text-align: right; }
    .small { font-size: 10px; color: #666; }
  </style>
  </head>
  <body>
    <div class="title">Contrôle prix d'AVOIR_traité_en_SL</div>
    <div class="meta">
      <div><span>COMPTE:</span> {{COMPTE}}</div>
      <div><span>CODE_ENT:</span> {{CODE_ENT}}</div>
      <div><span>CLIENT:</span> {{CLIENT}}</div>
      <div><span>REMARQUE:</span> {{REMARQUE}}</div>
      <div><span>DATE_AVR:</span> {{DATE_AVR}}</div>
      <div><span>CODE_COM:</span> {{CODE_COM}}</div>
      <div><span>REF_AVR_SL:</span> {{REF_AVR}}</div>
      <div><span>Page:</span> 1 / 1</div>
    </div>
    {{SECTIONS}}
  </body>
</html>`;

const renderPriceControlSections = (avoir, items, priceHistory) => {
  const historyByCode = new Map((priceHistory || []).map(h => [h.codeArt, h]));
  const itemsWithHistory = (items || []).filter(it => {
    const hist = historyByCode.get(it.CODE_ART);
    return Array.isArray(hist?.history) && hist.history.length > 0;
  });
  return itemsWithHistory.map((it, idx) => {
    const hist = historyByCode.get(it.CODE_ART);
    const lines = (hist?.history || []).map(h => `
      <tr>
        <td>${h.dateFac ? new Date(h.dateFac).toLocaleDateString('fr-FR') : ''}</td>
        <td>${h.refFac || ''}</td>
        <td class="right">${formatNumber(h.pvTTC)}</td>
        <td class="right">${formatNumber(h.remise)}%</td>
        <td class="right">${avoir.CODE_COM || ''}</td>
        <td class="right"></td>
        <td class="right">${Number(it.QTE) || 0}</td>
        <td class="right">Non</td>
      </tr>
    `).join('');

    return `
      <div class="section">
        <div class="section-header">
          <div class="num">${idx + 1}</div>
          <div class="code">${it.CODE_ART}</div>
          <div class="desc">${it.ARTICLE || ''}</div>
          <div class="qte small">QT_AVR: ${Number(it.QTE) || 0}</div>
        </div>
        <table>
          <thead>
            <tr>
              <th style="width: 12%">DATE_FAC</th>
              <th style="width: 16%">REF_FAC</th>
              <th class="right" style="width: 10%">PV_TTC</th>
              <th class="right" style="width: 8%">R%</th>
              <th class="right" style="width: 10%">Cd_com</th>
              <th class="right" style="width: 12%">vte_bolq</th>
              <th class="right" style="width: 10%">QT_AVR</th>
              <th class="right" style="width: 12%">Observation</th>
            </tr>
          </thead>
          <tbody>
            ${lines}
          </tbody>
        </table>
      </div>
    `;
  }).join('');
};

export const printAvoirPriceControl = async (avoir, items, priceHistory) => {
  try {
    const sectionsHtml = renderPriceControlSections(avoir, items || [], priceHistory || []);
    if (!sectionsHtml || sectionsHtml.trim() === '') {
      throw new Error('Aucune comparaison historique à imprimer');
    }
    const html = processTemplate(priceControlTemplate, {
      REF_AVR: avoir.REF_AVR || '',
      COMPTE: avoir.COMPTE || '',
      CODE_ENT: avoir.CODE_ENT || '',
      CLIENT: avoir.CLIENT || '',
      REMARQUE: avoir.REMARQUE || '',
      DATE_AVR: formatDate(avoir.DATE_AVR),
      CODE_COM: avoir.CODE_COM || '',
      SECTIONS: sectionsHtml
    });

    const iframe = createPrintFrame(html);
    iframe.onload = () => {
      iframe.contentWindow.print();
      setTimeout(() => { document.body.removeChild(iframe); }, 1000);
    };
  } catch (e) {
    console.error('Error printing price control:', e);
    throw e;
  }
};

// Price History Table Template - matches the drawer view design exactly
const priceHistoryTableTemplate = `
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8" />
  <title>Historique des prix - {{CLIENT}}</title>
  <style>
    @media print {
      @page { size: A4 portrait; margin: 10mm; }
      body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      .page-break { page-break-before: always; }
    }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { 
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      font-size: 14px; 
      color: #000; 
      padding: 0;
      line-height: 1.4;
    }
    .page {
      padding: 10px 0;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
      padding-bottom: 10px;
      border-bottom: 2px solid #ccc;
    }
    .header-title {
      font-size: 18px;
      font-weight: bold;
      color: #333;
    }
    .header-info {
      text-align: right;
      font-size: 12px;
      color: #666;
    }
    .container {
      border: 1px solid #f0f0f0;
      border-radius: 2px;
    }
    /* Main table header - grid style matching drawer */
    .main-header {
      display: grid;
      grid-template-columns: 100px 1fr 90px 80px;
      background-color: #fafafa;
      padding: 8px 16px;
      border-bottom: 2px solid #f0f0f0;
      font-size: 12px;
      font-weight: 600;
      gap: 0 20px;
    }
    .main-header > div {
      white-space: nowrap;
    }
    .main-header > div:nth-child(2) {
      padding-right: 20px;
    }
    /* Main article row */
    .main-row {
      display: grid;
      grid-template-columns: 100px 1fr 90px 80px;
      background-color: #fff;
      padding: 6px 16px;
      border-bottom: 1px solid #f0f0f0;
      font-size: 12px;
      align-items: center;
      gap: 0 20px;
    }
    .main-row > div {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .main-row > div:nth-child(2) {
      padding-right: 20px;
    }
    /* Nested history section */
    .nested-section {
      padding-left: 32px;
      background-color: #fafafa;
      border-bottom: 1px solid #f0f0f0;
    }
    .nested-header {
      display: grid;
      grid-template-columns: 90px 80px 70px 70px 60px 50px;
      background-color: #e6f7ff;
      padding: 5px 16px;
      border-bottom: 1px solid #d9d9d9;
      font-size: 11px;
      font-weight: 600;
    }
    .nested-header > div {
      white-space: nowrap;
    }
    .nested-row {
      display: grid;
      grid-template-columns: 90px 80px 70px 70px 60px 50px;
      padding: 4px 16px;
      border-bottom: 1px solid #d9d9d9;
      font-size: 11px;
      background-color: #fff;
      align-items: center;
    }
    .nested-row > div {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .nested-row:last-child {
      border-bottom: none;
    }
    .cold-indicator {
      display: inline-block;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      margin-right: 6px;
      flex-shrink: 0;
    }
    .cold { background-color: #1890ff; }
    .hot { background-color: #ff7a45; }
    .facture-cell {
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .page-footer {
      text-align: center;
      font-size: 11px;
      margin-top: 12px;
      color: #888;
      padding-top: 8px;
      border-top: 1px solid #eee;
    }
    .item-block {
      border-bottom: 1px solid #f0f0f0;
    }
    .item-block:last-child {
      border-bottom: none;
    }
  </style>
</head>
<body>
  {{PAGES}}
</body>
</html>`;

const priceHistoryPageTemplate = `
<div class="page {{PAGE_CLASS}}">
  <div class="header">
    <div class="header-title">Historique des prix - 3 dernières années</div>
    <div class="header-info">
      <div><strong>Client:</strong> {{CLIENT}} ({{COMPTE}})</div>
      <div><strong>Date:</strong> {{PRINT_DATE}}</div>
    </div>
  </div>
  
  <div class="container">
    <div class="main-header">
      <div>Code Art</div>
      <div>Article</div>
      <div>PV cat. TTC</div>
      <div>PV avr. TTC</div>
    </div>
    {{ITEM_BLOCKS}}
  </div>
  
  <div class="page-footer">Page {{CURRENT_PAGE}} / {{TOTAL_PAGES}}</div>
</div>`;

const generatePriceHistoryBlocks = (priceHistory, currentItemByCode) => {
  return (priceHistory || []).map(ph => {
    const current = currentItemByCode[ph.codeArt] || {};
    const history = ph.history || [];
    
    // Main row for the article
    const mainRow = `
      <div class="main-row">
        <div>${ph.codeArt || ''}</div>
        <div>${ph.article?.ARTICLE || current?.ARTICLE || ''}</div>
        <div>${ph.article?.PV_TTC != null ? formatNumber(ph.article.PV_TTC) : ''}</div>
        <div>${current?.PV_TTC != null ? formatNumber(current.PV_TTC) : ''}</div>
      </div>
    `;
    
    // Nested history section
    let nestedHtml = '';
    if (history.length > 0) {
      const historyRows = history.map(h => {
        const dateFac = h.dateFac ? new Date(h.dateFac) : null;
        const formattedDate = dateFac 
          ? `${String(dateFac.getDate()).padStart(2, '0')}/${String(dateFac.getMonth() + 1).padStart(2, '0')}/${dateFac.getFullYear()}`
          : '';
        const coldClass = h.isCold ? 'cold' : 'hot';
        
        return `
          <div class="nested-row">
            <div class="facture-cell"><span class="cold-indicator ${coldClass}"></span>${h.refFac || ''}</div>
            <div>${formattedDate}</div>
            <div>${formatNumber(h.pvTTC)}</div>
            <div>${formatNumber(h.pvHT)}</div>
            <div>${formatNumber(h.remise)}%</div>
            <div>${Number(h.qte) || 0}</div>
          </div>
        `;
      }).join('');
      
      nestedHtml = `
        <div class="nested-section">
          <div class="nested-header">
            <div>Facture</div>
            <div>Date</div>
            <div>PV TTC</div>
            <div>PV HT</div>
            <div>Remise</div>
            <div>Qté</div>
          </div>
          ${historyRows}
        </div>
      `;
    }
    
    return `<div class="item-block">${mainRow}${nestedHtml}</div>`;
  }).join('');
};

export const printPriceHistoryTable = async (avoir, localItems, priceHistory) => {
  try {
    if (!priceHistory || priceHistory.length === 0) {
      throw new Error('Aucun historique de prix à imprimer');
    }

    // Build currentItemByCode map
    const currentItemByCode = {};
    if (Array.isArray(localItems)) {
      localItems.forEach(it => { 
        if (it?.CODE_ART) currentItemByCode[it.CODE_ART] = it; 
      });
    }

    // Calculate rows: each article = 1 row (main) + 1 row (nested header if has history) + N facture rows
    const ROWS_PER_PAGE = 33;
    const printDate = new Date().toLocaleDateString('fr-FR');
    
    // Split items into pages based on row count
    const pages = [];
    let currentPageItems = [];
    let currentRowCount = 0;
    
    for (const ph of priceHistory) {
      const historyCount = ph.history?.length || 0;
      // Count: 1 for main row + 1 for nested header (if has history) + number of facture rows
      const itemRowCount = 1 + (historyCount > 0 ? 1 + historyCount : 0);
      
      // If adding this item would exceed the limit and we have items, start a new page
      if (currentRowCount + itemRowCount > ROWS_PER_PAGE && currentPageItems.length > 0) {
        pages.push(currentPageItems);
        currentPageItems = [];
        currentRowCount = 0;
      }
      
      currentPageItems.push(ph);
      currentRowCount += itemRowCount;
    }
    
    // Don't forget the last page
    if (currentPageItems.length > 0) {
      pages.push(currentPageItems);
    }
    
    const totalPages = pages.length;
    let pagesHtml = '';
    
    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
      const pageItems = pages[pageNum - 1];
      
      const itemBlocks = generatePriceHistoryBlocks(pageItems, currentItemByCode);
      
      const pageHtml = processTemplate(priceHistoryPageTemplate, {
        PAGE_CLASS: pageNum < totalPages ? 'page-break' : '',
        CLIENT: avoir?.CLIENT || 'N/A',
        COMPTE: avoir?.COMPTE || 'N/A',
        PRINT_DATE: printDate,
        CURRENT_PAGE: pageNum,
        TOTAL_PAGES: totalPages,
        ITEM_BLOCKS: itemBlocks
      });
      
      pagesHtml += pageHtml;
    }

    const html = processTemplate(priceHistoryTableTemplate, {
      CLIENT: avoir?.CLIENT || 'N/A',
      PAGES: pagesHtml
    });

    const iframe = createPrintFrame(html);
    iframe.onload = () => {
      iframe.contentWindow.print();
      setTimeout(() => { document.body.removeChild(iframe); }, 1000);
    };
  } catch (e) {
    console.error('Error printing price history:', e);
    throw e;
  }
};